import { useNavigate } from "react-router-dom";
import { Download, Puzzle, ToggleRight, Sparkles, ArrowRight, Terminal } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LandingNav } from "@/components/landing/LandingNav";
import { LandingFooter } from "@/components/landing/LandingFooter";
import { PRIMARY, Reveal, Kicker } from "@/components/landing/shared";

// Public "connect quickly" guide (Phase 11). Static JSX text only — no markdown-to-HTML,
// no dangerouslySetInnerHTML (stored-XSS invariant holds even though this content is ours).
const STEPS = [
  { i: Download, t: "Download the skill", b: "On any of your skills in Runebox, open “Use in Claude apps” and click Download .zip (or just Download .zip). It’s already in the folder format Claude expects — no unzipping or renaming." },
  { i: Puzzle, t: "Open Customize → Skills", b: "In claude.ai or the Claude Desktop app, go to Customize → Skills, click +, choose Create skill, and upload the .zip you just downloaded." },
  { i: ToggleRight, t: "Toggle it on", b: "Claude reads the SKILL.md, shows the name and description, and switches it on. That’s it — the exact same steps on web and on desktop." },
];

export default function UseWithClaude() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const startTeam = () => navigate(user ? "/orgs/new" : "/signup?redirect=/orgs/new");
  const goLogin = () => navigate("/login");

  return (
    <div className="min-h-screen bg-[color:var(--rb-void)]">
      <LandingNav user={user} login={goLogin} startTeam={startTeam} navigate={navigate} />

      <main id="top" data-testid="use-with-claude">
        <section className="rb-grain relative overflow-hidden">
          <div className="pointer-events-none absolute inset-0" style={{ backgroundImage: "radial-gradient(680px circle at 50% -6%, rgba(45,212,191,0.12), transparent 55%)" }} />
          <div className="relative mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:py-20">
            <Reveal>
              <div className="text-center">
                <Kicker><Sparkles className="h-3.5 w-3.5" /> Claude.ai &amp; Claude Desktop</Kicker>
                <h1 className="rb-display mx-auto max-w-2xl text-4xl font-semibold leading-[1.08] tracking-tight text-[color:var(--rb-text)] sm:text-5xl">
                  Use your Runebox skills in the <span className="text-[color:var(--rb-accent)]">Claude apps</span>
                </h1>
                <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-[color:var(--rb-text-dim)]">
                  Your skills aren&rsquo;t just for the terminal. Get any skill into claude.ai or the Claude Desktop app in three steps &mdash; about a minute, no setup.
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        <section className="pb-4">
          <div className="mx-auto max-w-3xl px-4 sm:px-6">
            <div className="space-y-4">
              {STEPS.map(({ i: Icon, t, b }, idx) => (
                <Reveal key={t} delay={idx * 0.06}>
                  <div className="flex items-start gap-4 rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-6">
                    <div className="flex items-center gap-3">
                      <span className="rb-mono text-2xl font-semibold text-[color:rgba(94,234,212,0.28)]">{idx + 1}</span>
                      <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-[color:var(--rb-border)] bg-[color:var(--rb-surface)]">
                        <Icon className="h-5 w-5 text-[color:var(--rb-accent)]" />
                      </div>
                    </div>
                    <div>
                      <h3 className="rb-display text-lg font-semibold text-[color:var(--rb-text)]">{t}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-[color:var(--rb-text-dim)]">{b}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10">
          <div className="mx-auto max-w-3xl px-4 sm:px-6">
            <div className="rounded-2xl border border-[color:var(--rb-border)] bg-[color:var(--rb-obsidian)] p-6">
              <h3 className="rb-display text-sm font-semibold text-[color:var(--rb-text)]">Good to know</h3>
              <ul className="mt-3 space-y-2 text-sm text-[color:var(--rb-text-dim)]">
                <li>&bull; The steps are identical on claude.ai (web) and the Claude Desktop app.</li>
                <li>&bull; Skills you upload this way are private to your own Claude account.</li>
                <li>&bull; Only <b className="text-[color:var(--rb-text)]">skills</b> upload to the Claude apps. Agents, commands, and output styles are Claude Code features.</li>
              </ul>
              <div className="mt-4 flex items-start gap-2.5 rounded-xl border border-[color:rgba(45,212,191,0.3)] bg-[color:rgba(45,212,191,0.08)] p-3 text-sm text-[color:var(--rb-accent)]">
                <Terminal className="mt-0.5 h-4 w-4 shrink-0" />
                <span>Using Claude <b>Code</b>? Skip all of this &mdash; <span className="rb-mono">/runebox:sync</span> installs your whole catalog automatically, and keeps it current.</span>
              </div>
            </div>
          </div>
        </section>

        <section className="pb-20">
          <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
            <Button onClick={() => navigate(user ? "/my-skills" : "/marketplace")} className={`h-12 rounded-xl px-7 text-base ${PRIMARY}`} data-testid="use-with-claude-cta">
              {user ? "Go to My Artifacts" : "Browse the free gallery"} <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </section>
      </main>

      <LandingFooter login={goLogin} />
    </div>
  );
}
